import { computed, ref } from "vue";

export interface Assinatura {
  diasRestantes: number;
  plano: "Comercial" | "Facturacao";
  gratuita: boolean;
}

export interface FeAgt {
  dataConfiguracao: string;
  estado: "Activo" | "Inactivo";
}

export interface Comerciante {
  id: number | string;
  nome: string;
  codigo: string;
  nif: string;
  telefone: string;
  email: string;
  assinatura: Assinatura | null;
  apiKeyDevelopment: string | null;
  apiKeyProduction: string | null;
  feAgt: FeAgt | null;
  bannerEcraClientePOS: string | null;
  dataAlteracao: string | null;
}

export interface MinhasPermissoes {
  backoffice: string[];
  pos: string[];
}

export interface PermissaoInfo {
  codigo: string;
  label: string;
}

export interface PermissoesSistema {
  backoffice: PermissaoInfo[];
  pos: PermissaoInfo[];
}

export type UlemoLicence = "ONLINE" | "OFFLINE";

export interface Representante {
  nome: string;
  nif: string;
  telefone: string;
  email: string;
  endereco: string;
}

export type TerminalPOS = "Geral" | "Restaurante";

export interface SessaoPOS {
  id: number;
  dataAbertura: string;
  valorAbertura: number;
  estabelecimento: { nome: string; numero: string };
  caixa: { nome: string };
  terminal: TerminalPOS;
}

export interface UserAuth {
  nome: string;
  username: string;
  foto: string;
  id: number | string;
  grupo: string;
  comerciante: Comerciante;
  totalNotificacoesNaoLidas: number;
  minhasPermissoes: MinhasPermissoes;
  permissoesSistema: PermissoesSistema;
  estaEmProducao: boolean;
  ulemoLicence: UlemoLicence;
  representante: Representante | null;
  sessaoPOS: SessaoPOS | null;
}

const currentUser = ref<UserAuth | null>(null);
let ultimaActualizacao = 0;

const DURACAO_CACHE_SESSAO_MS = 60_000;

export function setCurrentUser(user: UserAuth) {
  currentUser.value = user;
  ultimaActualizacao = Date.now();
}

const CHAVE_LOGOUT_ENTRE_ABAS = "ulemo:sessaoTerminada";

export function clearSession() {
  currentUser.value = null;
  ultimaActualizacao = 0;
  try {
    localStorage.setItem(CHAVE_LOGOUT_ENTRE_ABAS, String(Date.now()));
  } catch {
    // localStorage indisponível (ex.: modo de navegação privada); ignora
  }
}

/** Regista um listener para quando a sessão é terminada noutro separador
 * (ex.: clique em "Terminar sessão"), via evento `storage`, para essa aba
 * limpar a sua sessão e navegar para o login de imediato, em vez de deixar
 * pedidos subsequentes falharem com erro de comunicação com o servidor. */
export function aoTerminarSessaoNoutraAba(callback: () => void) {
  window.addEventListener("storage", (evento) => {
    if (evento.key !== CHAVE_LOGOUT_ENTRE_ABAS) return;
    currentUser.value = null;
    ultimaActualizacao = 0;
    callback();
  });
}

/** Utilizador autenticado ainda válido em cache (actualizado há menos de
 * 1 minuto), para o guard de navegação evitar pedidos repetidos ao servidor. */
export function getUserAutenticadoEmCache(): UserAuth | null {
  if (!currentUser.value) return null;
  return Date.now() - ultimaActualizacao < DURACAO_CACHE_SESSAO_MS
    ? currentUser.value
    : null;
}

export function useSession() {
  return {
    currentUser,
    isAuthenticated: computed(() => currentUser.value !== null),
  };
}
